/**
 * The page's view of its own companion (ADR-142 decision 6, P2.26-T6): whether he has joined, whether he is out, and his
 * name, as the server's `companionState` last said, and the send home or call back the toggle and its key ask for. The
 * server is the only writer of the state: a request changes nothing here until the answer arrives, so the toggle never
 * shows a state the server did not take.
 */

/**
 * What the page holds of its companion.
 */
export interface CompanionSnapshot {
  /** Whether the character has joined a companion; false until the server says so. */
  readonly joined: boolean;
  /** Whether he is out beside the player. */
  readonly out: boolean;
  /** His name, empty before he has joined. */
  readonly name: string;
}

/**
 * The state of a character with no companion.
 */
const NONE: CompanionSnapshot = Object.freeze({ joined: false, out: false, name: "" });

/**
 * The companion's store: fed each `companionState`, read by the toggle, and asked to send him out or home.
 */
export class CompanionStore {
  private snapshot: CompanionSnapshot = NONE;
  private readonly listeners = new Set<() => void>();
  private send: ((out: boolean) => boolean) | null = null;

  /**
   * @param frozen Whether the player is frozen by a Game Master, whose requests are not sent.
   */
  public constructor(private readonly frozen: () => boolean = () => false) {}

  /**
   * Hands the store the way a `companionSet` is sent.
   *
   * @param send Sends one and answers whether it went.
   */
  public bindSend(send: (out: boolean) => boolean): void {
    this.send = send;
  }

  /**
   * Takes a `companionState`.
   *
   * @param out Whether he is out.
   * @param name His name, empty before he has joined.
   */
  public receive(out: boolean, name: string): void {
    this.snapshot = name === "" && !out ? NONE : { joined: name !== "", out, name };
    this.emit();
  }

  /**
   * Forgets the companion, as a session ends.
   */
  public reset(): void {
    this.snapshot = NONE;
    this.emit();
  }

  /**
   * The state now, one object until it changes.
   *
   * @returns The snapshot.
   */
  public getSnapshot = (): CompanionSnapshot => this.snapshot;

  /**
   * Calls back on each change.
   *
   * @param listener Called after a change.
   * @returns The call that stops it.
   */
  public subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Asks the server to send him out or home, the opposite of what the page holds.
   *
   * @returns Whether a request was sent: false before he has joined, while frozen or while no connection can send it.
   */
  public toggle(): boolean {
    const held = this.snapshot;
    if (!held.joined || this.frozen() || this.send === null) {
      return false;
    }
    return this.send(!held.out);
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
